import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  ArrowLeft,
  Image as ImageIcon,
  Hash,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { GetSingleTestimonialsThunk } from "../../redux/states/testimonial/thunk";

const ViewTestimonials = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleTestimonial, loading } = useSelector((state) => state.testimonials);

  useEffect(() => {
    dispatch(GetSingleTestimonialsThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading product details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleTestimonial) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Testimonials Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The Testimonials you are looking for is not available.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Testimonials List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleTestimonial.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {singleTestimonial.name}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleTestimonial.status}
                  </span>
                </div>
                <p className="text-gray-600 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  SKU: {singleTestimonial.sku} | UUID: {singleTestimonial.uuid}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Image and Stats */}
            <div className="lg:col-span-1 space-y-6">
              {/* Product Image */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  Testimonials Image
                </h2>
                {singleTestimonial.image ? (
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${singleTestimonial.image}`}
                      alt={singleTestimonial.name}
                      className="w-full h-64 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                    />
                    <div className="absolute top-3 right-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded-lg text-sm">
                      Featured
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 text-gray-400">
                    <ImageIcon className="w-16 h-16 mb-2" />
                    <p>No Image Available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Pricing Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Name: {singleTestimonial.name || "Not Define"}
                </h2>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Title: {singleTestimonial.title || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                 Designation: {singleTestimonial.designation || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Company Name: {singleTestimonial.company_name || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                 Message:{" "}
                  {singleTestimonial.message || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                 Rating:{" "}
                  {singleTestimonial.rating || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                 Created Date:{" "}
                  {singleTestimonial.created_at || "Not Define"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Helper Components -------------------- */

export default ViewTestimonials;
